import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendPasswordResetEmail } from '../../../utils/firebase.utils';
import { showAuthError } from '../../../utils/toast';
import ToastService from '../../../utils/toast';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Support routing from login when a Google-only user tries email/password login
  const isSetPasswordContext = searchParams.get('context') === 'set-password';
  const prefillEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(prefillEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string }>({});

  const validateForm = () => {
    const newErrors: { email?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!email.includes('@') || !email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || isLoading) return;

    setIsLoading(true);
    try {
      await sendPasswordResetEmail(email.trim());
      setEmailSent(true);
      ToastService.showSuccess('Password reset email sent! Please check your inbox.');
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        showAuthError('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        showAuthError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        showAuthError('Too many requests. Please try again later.');
      } else {
        showAuthError('Failed to send reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="rounded-3xl shadow-2xl p-8 text-center" style={{ background: 'var(--surface-primary)' }}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-3xl">
                <i className="fas fa-check" />
              </span>
            </div>

            <h1 className="text-2xl font-bold mb-4 font-inter">Check Your Email</h1>

            <p className="text-gray-600 mb-4">
              We've sent a {isSetPasswordContext ? 'password setup' : 'password reset'} link to{' '}
              <strong>{email}</strong>
            </p>

            {isSetPasswordContext && (
              <p className="text-gray-600 mb-4">
                After setting your password, you'll be able to sign in with email and password in addition to Google.
              </p>
            )}

            <p className="text-sm text-gray-500 mb-8">
              Didn't receive the email? Check your spam folder or try again.
            </p>

            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold text-base rounded-lg h-12 flex items-center justify-center transition-all"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-key" />
            </span>
          </div>

          <h1 className="text-2xl font-bold text-center mb-2 font-inter">
            {isSetPasswordContext ? 'Set Your Password' : 'Reset Your Password'}
          </h1>

          <p className="text-gray-600 text-center mb-8">
            {isSetPasswordContext
              ? "It looks like your account was created with Google. Enter your email and we'll send you a link to set a password so you can also sign in with email."
              : "Enter your email address and we'll send you a link to reset your password."}
          </p>

          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: undefined });
                }}
                placeholder="Email address"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-transparent ${
                  errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${
                isLoading
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
              } text-white font-bold text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <span>{isSetPasswordContext ? 'Send Setup Link' : 'Send Reset Link'}</span>
              )}
            </button>
          </form>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/login')}
              className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
              disabled={isLoading}
            >
              ← Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
